/**
 * Partners Service
 * Handles all partner-related API operations
 */

import { supabase } from "./supabase";
import type {
  PaginatedResponse,
  PaginationParams,
  UUID,
} from "../types/common.types";
import type {
  Partner,
  PartnerWithStats,
  PartnerStats,
  PartnerFormData,
  PartnerFilters,
  AffiliateApiKey,
  AffiliateApiKeyFormData,
  AffiliateApiKeyWithSecret,
} from "../types/partner.types";

// =============================================================================
// Partner CRUD
// =============================================================================

/**
 * Get all partners with pagination and filtering
 */
export const getPartners = async (
  params?: PaginationParams,
  filters?: PartnerFilters,
): Promise<PaginatedResponse<PartnerWithStats>> => {
  const {
    page = 1,
    limit = 25,
    sortBy = "name",
    sortOrder = "asc",
  } = params || {};
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  let query = supabase
    .from("partners")
    .select("*", { count: "exact" })
    .eq("is_deleted", false)
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `name.ilike.*${filters.search}*,title.ilike.*${filters.search}*`,
    );
  }

  if (filters?.is_active !== undefined) {
    query = query.eq("is_active", filters.is_active);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  // Fetch stats for each partner
  const partnersWithStats: PartnerWithStats[] = await Promise.all(
    (data || []).map(async (partner) => {
      const stats = await getPartnerStats(partner.id);
      return { ...partner, ...stats };
    }),
  );

  return {
    data: partnersWithStats,
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Get a single partner by ID
 */
export const getPartner = async (id: UUID): Promise<Partner> => {
  const { data, error } = await supabase
    .from("partners")
    .select("*")
    .eq("id", id)
    .eq("is_deleted", false)
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Get partner stats
 */
export const getPartnerStats = async (
  partnerId: UUID,
): Promise<PartnerStats> => {
  const { data, error } = await supabase.rpc("get_partner_stats", {
    p_partner_id: partnerId,
  });

  if (error) {
    throw error;
  }

  return (
    data?.[0] || {
      total_affiliates: 0,
      active_affiliates: 0,
      total_contacts: 0,
    }
  );
};

/**
 * Create a new partner
 */
export const createPartner = async (
  partner: PartnerFormData,
): Promise<Partner> => {
  const { data, error } = await supabase
    .from("partners")
    .insert({
      ...partner,
      is_active: partner.is_active ?? true,
      is_deleted: false,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Update a partner
 */
export const updatePartner = async (
  id: UUID,
  updates: Partial<PartnerFormData>,
): Promise<Partner> => {
  const { data, error } = await supabase
    .from("partners")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Soft delete a partner (set is_deleted = true)
 */
export const deletePartner = async (id: UUID): Promise<void> => {
  const { error } = await supabase
    .from("partners")
    .update({
      is_deleted: true,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    throw error;
  }
};

/**
 * Toggle partner active status
 */
export const togglePartnerActive = async (
  id: UUID,
  isActive: boolean,
): Promise<Partner> => {
  const { data, error } = await supabase
    .from("partners")
    .update({
      is_active: isActive,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

// =============================================================================
// Partner API Keys
// =============================================================================

/**
 * Get API keys for a partner
 */
export const getPartnerApiKeys = async (
  partnerId: UUID,
): Promise<AffiliateApiKey[]> => {
  const { data, error } = await supabase
    .from("affiliate_api_keys")
    .select("*")
    .eq("partner_id", partnerId)
    .is("revoked_at", null)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
};

/**
 * Create a new API key for a partner
 * Returns the key with secret (only time secret is visible)
 */
export const createPartnerApiKey = async (
  keyData: AffiliateApiKeyFormData,
): Promise<AffiliateApiKeyWithSecret> => {
  // Generate a random API key
  const secret = generateApiKey();
  const keyPrefix = secret.substring(0, 12);
  const keyHash = await hashApiKey(secret);

  const { data, error } = await supabase
    .from("affiliate_api_keys")
    .insert({
      partner_id: keyData.partner_id,
      name: keyData.name,
      key_hash: keyHash,
      key_prefix: keyPrefix,
      rate_limit_per_minute: keyData.rate_limit_per_minute || 60,
      rate_limit_per_day: keyData.rate_limit_per_day || 10000,
      expires_at: keyData.expires_at || null,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    apiKey: data,
    secret: secret,
  };
};

/**
 * Revoke an API key
 */
export const revokeApiKey = async (id: UUID): Promise<void> => {
  const { error } = await supabase
    .from("affiliate_api_keys")
    .update({
      is_active: false,
      revoked_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    throw error;
  }
};

/**
 * Toggle API key active status
 */
export const toggleApiKeyActive = async (
  id: UUID,
  isActive: boolean,
): Promise<AffiliateApiKey> => {
  const { data, error } = await supabase
    .from("affiliate_api_keys")
    .update({
      is_active: isActive,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Generate a random API key
 */
function generateApiKey(): string {
  const prefix = "aff_live_";
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let key = prefix;
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

/**
 * Hash an API key using SHA-256
 */
async function hashApiKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// =============================================================================
// Dropdown Options
// =============================================================================

/**
 * Get partners for dropdown selection
 */
export const getPartnersForDropdown = async (): Promise<
  Array<{ value: UUID; label: string }>
> => {
  const { data, error } = await supabase
    .from("partners")
    .select("id, name")
    .eq("is_deleted", false)
    .eq("is_active", true)
    .order("name");

  if (error) {
    throw error;
  }

  return (data || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
};
